import React, { memo } from 'react';
import { getBlockDefinition } from '../core/registry.js';
import { useEditorActions } from '../store/editorStore.jsx';
import { BlockFrame } from './BlockFrame.jsx';
import { Inserter } from './Inserter.jsx';

/**
 * BlockRenderer
 *
 * Memoized component that renders a single block using its registered Canvas
 * component. Container blocks recursively render their children.
 *
 * Props:
 *   blockId      {string}      – id of the block to render
 *   doc          {object}      – the live doc object (passed down)
 *   parentId     {string|null} – parent container id (null = root)
 *   index        {number}      – position within the parent's children / root list
 *   siblingCount {number}      – total items in the parent list
 */
export const BlockRenderer = memo(function BlockRenderer({
  blockId,
  doc,
  parentId,
  index,
  siblingCount,
}) {
  const { moveBlock } = useEditorActions();
  const block = doc.blocks[blockId];

  if (!block) return null;

  const def = getBlockDefinition(block.type);
  if (!def || !def.ui || !def.ui.Canvas) {
    return (
      <div className="border rounded p-2 text-danger small">
        Unknown block type: <code>{block.type}</code>
      </div>
    );
  }

  const CanvasComponent = def.ui.Canvas;

  // Container blocks render their children recursively.
  let childrenNodes = null;
  if (Array.isArray(block.children)) {
    if (block.children.length === 0) {
      childrenNodes = <EmptyContainerHint parentId={block.id} />;
    } else {
      childrenNodes = block.children.map((childId, childIndex) => (
        <BlockRenderer
          key={childId}
          blockId={childId}
          doc={doc}
          parentId={block.id}
          index={childIndex}
          siblingCount={block.children.length}
        />
      ));
    }
  }

  const handleDropBefore = (draggedId, targetIndex) => {
    moveBlock(draggedId, parentId, targetIndex);
  };

  return (
    <BlockFrame
      block={block}
      parentId={parentId}
      index={index}
      siblingCount={siblingCount}
      onDropBefore={handleDropBefore}
    >
      <CanvasComponent block={block}>
        {childrenNodes}
      </CanvasComponent>
    </BlockFrame>
  );
});

function EmptyContainerHint({ parentId }) {
  const { insertBlock } = useEditorActions();
  const [open, setOpen] = React.useState(false);

  return (
    <div
      className="text-center p-3 text-muted small"
      style={{ border: '1px dashed #ced4da', borderRadius: 4, position: 'relative' }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={() => setOpen((v) => !v)}
      >
        + Add block
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 100 }}>
          <Inserter
            parentId={parentId}
            index={0}
            onInsert={(type) => { insertBlock(parentId, 0, type); setOpen(false); }}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
